/**
 * Telemetry service — Sentry error tracking with privacy scrubbing.
 *
 * Privacy rules (non-negotiable):
 * - Never capture schema content, file/package names, search queries, PII
 * - URLs are scrubbed to remove entity IDs
 * - Breadcrumbs use generic labels only
 * - See docs/feature-level-specs/telemetry-error-tracking.md for full rules
 */

import * as Sentry from '@sentry/svelte';

/**
 * Replace entity-specific URL segments with generic placeholders.
 * Strips query strings entirely (may contain schema identifiers).
 *
 * Note: The design spec's example regex assumed a two-segment pattern
 * (/table/:version/:id) but actual routes use a single segment
 * (/table/[id], /enum/[id]). This regex matches the real route structure.
 */
export function scrubUrl(url: string): string {
  return url
    .replace(/\/(table|enum)\/[^/?#]+/g, '/$1/:id')
    .replace(/\?.*$/, '');
}

/**
 * Minimal Breadcrumb shape — avoids importing Sentry types so scrubbing
 * functions can be tested without mocking the SDK. Kept in sync with
 * Sentry.Breadcrumb manually; if the SDK changes its shape, update here.
 */
export interface Breadcrumb {
  category?: string;
  message?: string;
  level?: string;
  data?: Record<string, unknown>;
}

/**
 * Scrub a breadcrumb before it is sent to Sentry.
 * - Navigation breadcrumbs: scrub from/to URLs
 * - XHR/fetch breadcrumbs: scrub request URL
 * - Console breadcrumbs with data payloads: drop entirely (may contain schema data)
 * - Returns null to drop the breadcrumb.
 */
export function scrubBreadcrumb(breadcrumb: Breadcrumb | undefined): Breadcrumb | null {
  if (!breadcrumb) return null;

  // Drop console breadcrumbs that carry data payloads (may contain schema content)
  if (breadcrumb.category === 'console' && breadcrumb.data) {
    return null;
  }

  // Scrub navigation URLs
  if (breadcrumb.category === 'navigation' && breadcrumb.data) {
    return {
      ...breadcrumb,
      data: {
        ...breadcrumb.data,
        ...(typeof breadcrumb.data.from === 'string' && { from: scrubUrl(breadcrumb.data.from) }),
        ...(typeof breadcrumb.data.to === 'string' && { to: scrubUrl(breadcrumb.data.to) })
      }
    };
  }

  // Scrub XHR/fetch URLs
  if ((breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') && breadcrumb.data) {
    return {
      ...breadcrumb,
      data: {
        ...breadcrumb.data,
        ...(typeof breadcrumb.data.url === 'string' && { url: scrubUrl(breadcrumb.data.url) })
      }
    };
  }

  return breadcrumb;
}

const TELEMETRY_KEY = 'telemetry-errors';

/** Check if telemetry is enabled. Default: true (opt-out model). */
export function isTelemetryEnabled(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(TELEMETRY_KEY) !== 'disabled';
}

/** Persist the telemetry preference to localStorage. */
export function setTelemetryEnabled(enabled: boolean): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(TELEMETRY_KEY, enabled ? 'enabled' : 'disabled');
}

/**
 * Exhaustive allowlist of metadata permitted on handled errors.
 * Adding a new field here requires review against privacy rules
 * (docs/feature-level-specs/telemetry-error-tracking.md).
 */
export interface SafeErrorContext {
  // Storage errors
  operation?: 'read' | 'write' | 'migration' | 'export' | 'import';
  store?: string;

  // Upload errors
  stage?: 'zip-extract' | 'json-parse' | 'validation' | 'indexing';
  fileSizeMB?: number;

  // General
  tableCount?: number;
  fieldCount?: number;
  durationMs?: number;
  errorCode?: string;
}

let sentryInitialized = false;

/**
 * Initialize Sentry. Guards on:
 * 1. Production environment (import.meta.env.PROD)
 * 2. User telemetry preference (localStorage)
 * 3. DSN being configured
 */
export function initSentry(): void {
  if (sentryInitialized) return;
  if (!import.meta.env.PROD) return;
  if (!isTelemetryEnabled()) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    release: import.meta.env.VITE_APP_VERSION || undefined,
    environment: 'production',

    beforeSend(event) {
      // Scrub URLs
      if (event.request?.url) {
        event.request.url = scrubUrl(event.request.url);
      }
      // Scrub breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs
          .map((b) => scrubBreadcrumb(b as Breadcrumb))
          .filter((b): b is Breadcrumb => b !== null);
      }
      // Never send user identity
      delete event.user;
      return event;
    },

    beforeBreadcrumb(breadcrumb) {
      return scrubBreadcrumb(breadcrumb as Breadcrumb) as Sentry.Breadcrumb | null;
    },
  });

  sentryInitialized = true;
}

/** Flush pending events and shut down Sentry. */
export async function closeSentry(): Promise<void> {
  await Sentry.close();
  sentryInitialized = false;
}

/**
 * Capture a handled error with safe metadata tags.
 * Returns the Sentry event ID (for Diagnostic ID display) or undefined
 * if Sentry is not initialized.
 */
export function captureExpectedError(
  error: Error,
  context: SafeErrorContext
): string | undefined {
  if (!sentryInitialized) return undefined;
  return Sentry.captureException(error, {
    tags: Object.fromEntries(
      Object.entries(context).filter(([, v]) => v !== undefined)
    ),
  });
}

/**
 * Capture an unhandled error (from error boundary).
 * Returns the Sentry event ID for Diagnostic ID display, or undefined.
 */
export function captureUnhandledError(error: Error): string | undefined {
  if (!sentryInitialized) return undefined;
  return Sentry.captureException(error);
}

/** Whether Sentry has been initialized in this session. */
export function isSentryInitialized(): boolean {
  return sentryInitialized;
}
